import { useDispatch, useSelector } from 'react-redux';
import ccfCopilotCardStyles from './ccf-live-copilot.styles';
import { useTheme, TextField, InputAdornment, Box } from '@mui/material';
import { useTranslator, CcfSearchIcon } from '@nice-ccf/ui-controls';
import { useEffect, useState } from 'react';
import { activeContactSelector } from '../../ccf-inbox/ccf-inbox.slice';
import { AGENT_SEARCH, API_BASE_URL, callApi } from './ccf-copilot-api';
import { processApiResponse } from './ccf-copilot-util';
import {
  getAgentSearchTopic,
  setAgentSearchTopic,
  setIsRequestProcessing,
} from './ccf-copilot-chat/+state/ccf-live-copilot.slice';
import { v5 as uuidv5, NIL as namespaceNIL } from 'uuid';
import {
  getcopilotFlowMsgIndex,
  updateFlowMsgIndex,
} from './ccf-copilot-chat/+state/ccf-copilot.slice';

/**
 * Component displays copilot search container
 * @example
 * ```
 * <CcfAgentCopilotSearchContainer/>
 * ```
 */
export function CcfAgentCopilotSearchContainer() {
  const [translate] = useTranslator();
  const dispatch = useDispatch();
  const theme = useTheme();
  const cardStyles = ccfCopilotCardStyles(theme);
  const searchTextFromSelector = useSelector(getAgentSearchTopic);
  const [searchText, setSearchText] = useState(searchTextFromSelector ?? '');
  const activeContact = useSelector(activeContactSelector);
  const flowMsgIndex = useSelector(getcopilotFlowMsgIndex);
  const namespace = uuidv5('namespace', namespaceNIL);

  useEffect(() => {
    setSearchText(searchTextFromSelector);
  }, [searchTextFromSelector]);

  useEffect(() => {
    if (searchText === '') {
      dispatch(setAgentSearchTopic(''));
    }
  }, [searchText]);

  /**
   * Used handle agent search query
   * @param query -string
   * @example - handleAgentSearch(query)
   */
  const handleAgentSearch = (query: string) => {
    const requestBody = {
      agentId: '1898',
      tenantId: '12345678',
      contactId: activeContact?.id,
      query: query,
      busNo: '12345678',
      channel: activeContact?.media === 'Voice' ? 'Voice' : 'Digital',
      transcriptionEventId: uuidv5(query, namespace),
      getKbCombo: false,
      getProcessSteps: false,
    };

    // dispatch(setIsRequestProcessing(true));
    callApi(
      API_BASE_URL + AGENT_SEARCH,
      'POST',
      { ...requestBody, getKbCombo: true },
      dispatch,
      true
    )
      .then((response: any) => {
        const matchingConfidence = activeContact?.confidence?.find((item: any) => 
          query?.toLowerCase().includes(item.statement.toLowerCase())
        );
        
        const showGreenConfidenceDot = matchingConfidence ? matchingConfidence.value === 'High' : false;
        callApi(
          API_BASE_URL + AGENT_SEARCH,
          'POST',
          { ...requestBody, getProcessSteps: true },
          dispatch,
          true
        )
          .then((response: any) => {
            processApiResponse(response.data, dispatch, 'Digital', true, false, showGreenConfidenceDot);
          })
          .catch((error: any) => {
            console.error(
              'There was a problem with the fetch operation from copilot:',
              error
            );
          });
          processApiResponse(response.data, dispatch, 'Digital', false, false, showGreenConfidenceDot);
      })
      .catch((error: any) => {
        console.error(
          'There was a problem with the fetch operation from copilot:',
          error
        );
      })
      .finally(() => {
        dispatch(setIsRequestProcessing(false));
      });
  };
  /**
   * Function to submit search text
   * @example
   * ```
   * onSubmit()
   * ```
   */
  const onSubmit = () => {
    //(agentSearchTopic || searchText) && handleAgentSearch(agentSearchTopic !== ''?agentSearchTopic : searchText);
    if (activeContact?.copilotSource === 'JSON') {
      //dispatch(updateFlowMsgIndex(flowMsgIndex + 1));
      setTimeout(() => {
        setSearchText('');
      }, 2000);
    } else {
      searchText && handleAgentSearch(searchText);
      setSearchText('');

      dispatch(setAgentSearchTopic(''));
    }
  };

  const agentSearchPlaceHolderText = translate('howCanIHelp');

  return (
    <TextField
      sx={cardStyles.textField}
      placeholder={agentSearchPlaceHolderText}
      value={searchText}
      id="searchInput"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <CcfSearchIcon
              fontSize="small"
              onClick={() => onSubmit()}
              sx={cardStyles.searchIcon}
              viewBox="0 0 32 32"
              data-testid="inputSearchIcon"
            />
          </InputAdornment>
        ),
      }}
      onChange={(event: React.ChangeEvent<{ name: string; value: string }>) =>
        setSearchText(event.target.value as string)
      }
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
        event.key === 'Enter' && onSubmit();
      }}
      variant="outlined"
      role="search"
    />
  );
}
export default CcfAgentCopilotSearchContainer;
